// src/components/setup/steps/SetupComplete.jsx
import React from 'react';
import { CheckCircle, Rocket, ArrowRight, Gift, Store } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import styles from './StepComponents.module.css';

const SetupComplete = ({ user, formData, onNext, isSubmitting, isResellerFlow }) => {
  const { t } = useLocalization();

  const providerName = user?.business?.setupProgress?.dropshippingProviderHash
    ? formData?.providerName || ''
    : '';

  const normalNextSteps = [
    { titleKey: 'setup.nextStep1Title', descKey: 'setup.nextStep1Description' },
    { titleKey: 'setup.nextStep2Title', descKey: 'setup.nextStep2Description' },
    { titleKey: 'setup.nextStep3Title', descKey: 'setup.nextStep3Description' },
  ];

  const resellerNextSteps = [
    { titleKey: 'setup.resellerNextStep1Title', descKey: 'setup.resellerNextStep1Description' },
    { titleKey: 'setup.resellerNextStep2Title', descKey: 'setup.resellerNextStep2Description' },
    { titleKey: 'setup.resellerNextStep3Title', descKey: 'setup.resellerNextStep3Description' },
  ];

  const nextSteps = isResellerFlow ? resellerNextSteps : normalNextSteps;

  return (
    <div className={styles.stepContainer}>
      <div className={styles.completionHeader}>
        <div className={styles.successIcon}>
          <CheckCircle size={64} />
        </div>
        <h1>{t('setup.setupCompleteTitle')}</h1>
        <p className={styles.successMessage}>
          {isResellerFlow && providerName
            ? t('setup.resellerSetupCompleteMessage', { providerName })
            : t('setup.setupCompleteMessage', { businessName: user?.business?.name })}
        </p>
      </div>

      <div className={styles.setupSummary}>
        <h2>{t('setup.setupSummaryTitle')}</h2>
        <div className={styles.completedItems}>
          <div className={styles.completedItem}>
            <CheckCircle className={styles.completedIcon} size={20} />
            <span>{t('setup.emailVerificationComplete')}</span>
          </div>
          <div className={styles.completedItem}>
            <CheckCircle className={styles.completedIcon} size={20} />
            <span>{t('setup.businessProfileComplete')}</span>
          </div>

          {isResellerFlow ? (
            <>
              <div className={styles.completedItem}>
                <CheckCircle className={styles.completedIcon} size={20} />
                <span>{t('dropshipping.kycDocuments')}</span>
              </div>
              {providerName && (
                <div className={styles.completedItem}>
                  <Store className={styles.completedIcon} size={20} />
                  <span>{t('setup.resellerConnectedComplete', { providerName })}</span>
                </div>
              )}
            </>
          ) : (
            <>
              {formData.shop && (
                <div className={styles.completedItem}>
                  <CheckCircle className={styles.completedIcon} size={20} />
                  <span>{t('setup.shopCreatedComplete', { shopName: formData.shop.name })}</span>
                </div>
              )}
              {formData.warehouse && (
                <div className={styles.completedItem}>
                  <CheckCircle className={styles.completedIcon} size={20} />
                  <span>{t('setup.warehouseCreatedComplete', { warehouseName: formData.warehouse.name })}</span>
                </div>
              )}
              {formData.assignedCouriers ? (
                <div className={styles.completedItem}>
                  <CheckCircle className={styles.completedIcon} size={20} />
                  <span>{t('setup.couriersAssignedComplete', { count: formData.assignedCouriers.length })}</span>
                </div>
              ) : formData.couriersSkipped ? (
                <div className={styles.skippedItem}>
                  <span>{t('setup.couriersSkippedComplete')}</span>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>

      {/* Next Steps */}
      <div className={styles.nextSteps}>
        <h3>{t('setup.nextStepsTitle')}</h3>
        <div className={styles.nextStepsList}>
          {nextSteps.map((step, index) => (
            <div key={index} className={styles.nextStepItem}>
              <div className={styles.stepNumber}>{index + 1}</div>
              <div className={styles.stepContent}>
                <h4>{t(step.titleKey)}</h4>
                <p>{t(step.descKey)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Welcome bonus */}
      <div className={styles.welcomeBonus}>
        <Gift className={styles.giftIcon} size={24} />
        <div className={styles.bonusContent}>
          <h4>{t('setup.welcomeBonusTitle')}</h4>
          <p>{t('setup.welcomeBonusDescription')}</p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className={styles.stepActions}>
        <button
          onClick={onNext}
          disabled={isSubmitting}
          className={styles.primaryButton}
        >
          {isSubmitting ? (
            <>
              <div className={styles.loadingSpinner}></div>
              {t('setup.finishing')}
            </>
          ) : (
            <>
              <Rocket size={20} />
              {t('setup.getStarted')}
              <ArrowRight size={20} />
            </>
          )}
        </button>
      </div>

      {/* Support Information */}
      <div className={styles.supportInfo}>
        <h4>{t('setup.needHelpTitle')}</h4>
        <p>{t('setup.supportDescription')}</p>
        <div className={styles.supportLinks}>
          <a href="/docs" className={styles.supportLink}>{t('setup.viewDocumentation')}</a>
          <a href="/support" className={styles.supportLink}>{t('setup.contactSupport')}</a>
          <a href="/tutorials" className={styles.supportLink}>{t('setup.watchTutorials')}</a>
        </div>
      </div>
    </div>
  );
};

export default SetupComplete;
